import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Pagination,
  Modal,
  Badge
} from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { snapshotsAPI, cameraAPI, classroomAPI, analysisPromptAPI, snapshotAnalysisAPI } from '../utils/api';
import { formatDateTimeInNurseryTimezone } from '../utils/timezone';
import type { Camera } from '../types/camera';
import type { Classroom } from '../types/classroom';
import type { Snapshot, SnapshotFilters, SnapshotPaginationMeta, AnalysisPrompt, SnapshotAnalysis } from '../types/snapshot';

const markdownComponents = {
  td: ({ children, ...props }: React.HTMLAttributes<HTMLTableCellElement>) => {
    const text = String(children ?? '').trim();
    const isPass = /^Pass$/i.test(text);
    const isFail = /^Fail/i.test(text);
    if (isPass) {
      return <td {...props}><span className="text-success fw-bold"><i className="bi bi-check-circle-fill me-1"></i>{text}</span></td>;
    }
    if (isFail) {
      return <td {...props}><span className="text-danger fw-bold"><i className="bi bi-x-circle-fill me-1"></i>{text}</span></td>;
    }
    return <td {...props}>{children}</td>;
  },
};

const CameraSnapshotsPage: React.FC = () => {
  const { currentNursery } = useAuth();
  const { canViewSnapshots, canDeleteSnapshots, canAnalyzeSnapshots } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'cameraSnapshots' });
  const { t: ta } = useTranslation('pages', { keyPrefix: 'snapshotAnalysis' });
  const { t: tc } = useTranslation('common');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [pagination, setPagination] = useState<SnapshotPaginationMeta | null>(null);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());

  // AI Analysis state
  const [prompts, setPrompts] = useState<AnalysisPrompt[]>([]);
  const [showAnalyzeModal, setShowAnalyzeModal] = useState<Snapshot | null>(null);
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<SnapshotAnalysis | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState<Snapshot | null>(null);
  const [analysisHistory, setAnalysisHistory] = useState<SnapshotAnalysis[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Filter state
  const [formState, setFormState] = useState<{
    classroom_id: string;
    camera_id: string;
    date_from: string;
    date_to: string;
  }>({
    classroom_id: '',
    camera_id: '',
    date_from: '',
    date_to: ''
  });

  const [appliedFilters, setAppliedFilters] = useState<SnapshotFilters>({
    page: 1,
    per_page: 24
  });

  const [success, setSuccess] = useState<string | null>(null);

  // Modals
  const [selectedSnapshot, setSelectedSnapshot] = useState<Snapshot | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<number | null>(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showSelectedDeleteModal, setShowSelectedDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedSnapshots, setSelectedSnapshots] = useState<Set<number>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);

  const clearSelection = () => {
    setSelectedSnapshots(new Set());
    setSelectionMode(false);
  };

  const fetchFilterData = useCallback(async () => {
    if (!currentNursery) return;
    try {
      const [camerasRes, classroomsRes] = await Promise.all([
        cameraAPI.getCameras(currentNursery.nursery_id),
        classroomAPI.getClassrooms(currentNursery.nursery_id)
      ]);
      setCameras(camerasRes.cameras || []);
      setClassrooms(classroomsRes.classrooms || []);
    } catch (err) {
      console.error('Error fetching snapshot filters:', err);
    }
  }, [currentNursery]);

  const fetchPrompts = useCallback(async () => {
    if (!currentNursery) return;
    try {
      const res = await analysisPromptAPI.listPrompts(currentNursery.nursery_id);
      setPrompts(res.data || []);
    } catch (err) {
      console.error('Error fetching prompts:', err);
    }
  }, [currentNursery]);

  const fetchSnapshots = useCallback(async () => {
    if (!currentNursery) return;
    try {
      setLoading(true);
      setError(null);
      const res = await snapshotsAPI.listSnapshots(currentNursery.nursery_id, appliedFilters);
      setSnapshots(res.data || []);
      setPagination(res.pagination);
    } catch (err: any) {
      console.error('Error fetching snapshots:', err);
      setError(t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery, appliedFilters, t]);

  useEffect(() => {
    if (canViewSnapshots) {
      fetchFilterData();
      if (canAnalyzeSnapshots) {
        fetchPrompts();
      }
    }
  }, [fetchFilterData, fetchPrompts, canViewSnapshots, canAnalyzeSnapshots]);

  useEffect(() => {
    if (canViewSnapshots) {
      fetchSnapshots();
    }
  }, [fetchSnapshots, canViewSnapshots]);

  const handleFilterChange = (field: string, value: string) => {
    setFormState(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'classroom_id' ? { camera_id: '' } : {})
    }));
  };

  const applyFilters = () => {
    clearSelection();
    setAppliedFilters(prev => ({
      ...prev,
      classroom_id: formState.classroom_id ? parseInt(formState.classroom_id) : undefined,
      camera_id: formState.camera_id ? parseInt(formState.camera_id) : undefined,
      date_from: formState.date_from || undefined,
      date_to: formState.date_to || undefined,
      page: 1
    }));
  };

  const clearFilters = () => {
    clearSelection();
    setFormState({
      classroom_id: '',
      camera_id: '',
      date_from: '',
      date_to: ''
    });
    setAppliedFilters({
      page: 1,
      per_page: 24
    });
  };

  const handlePageChange = (page: number) => {
    clearSelection();
    setAppliedFilters(prev => ({ ...prev, page }));
    window.scrollTo(0, 0);
  };

  const toggleSelectionMode = () => {
    setSelectionMode(prev => {
      if (prev) {
        setSelectedSnapshots(new Set());
      }
      return !prev;
    });
  };

  const toggleSnapshotSelection = (snapshotId: number) => {
    setSelectedSnapshots(prev => {
      const next = new Set(prev);
      if (next.has(snapshotId)) {
        next.delete(snapshotId);
      } else {
        next.add(snapshotId);
      }
      return next;
    });
  };

  const selectAllSnapshots = () => {
    setSelectedSnapshots(new Set(snapshots.map(snapshot => snapshot.id)));
  };

  const deselectAllSnapshots = () => {
    setSelectedSnapshots(new Set());
  };

  const handleDeleteSingle = async () => {
    if (!currentNursery || !showDeleteModal) return;
    try {
      setDeleting(true);
      setError(null);
      setSuccess(null);
      await snapshotsAPI.deleteSnapshot(currentNursery.nursery_id, showDeleteModal);
      setShowDeleteModal(null);
      setSuccess(t('deleteSuccess'));
      fetchSnapshots();
    } catch (err) {
      console.error('Error deleting snapshot:', err);
      setError(t('deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!currentNursery) return;
    try {
      setDeleting(true);
      setError(null);
      setSuccess(null);
      await snapshotsAPI.bulkDeleteSnapshots(currentNursery.nursery_id, {
        camera_id: formState.camera_id ? parseInt(formState.camera_id) : undefined,
        classroom_id: formState.classroom_id ? parseInt(formState.classroom_id) : undefined,
        date_from: formState.date_from,
        date_to: formState.date_to
      });
      setShowBulkDeleteModal(false);
      setSuccess(t('bulkDeleteSuccess'));
      fetchSnapshots();
    } catch (err) {
      console.error('Error bulk deleting snapshots:', err);
      setError(t('deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!currentNursery || selectedSnapshots.size === 0) return;
    const deletedCount = selectedSnapshots.size;
    try {
      setDeleting(true);
      setError(null);
      setSuccess(null);
      await snapshotsAPI.bulkDeleteSnapshots(currentNursery.nursery_id, {
        snapshot_ids: Array.from(selectedSnapshots),
        classroom_id: formState.classroom_id ? parseInt(formState.classroom_id) : undefined
      });
      setShowSelectedDeleteModal(false);
      clearSelection();
      setSuccess(t('deleteSelectedSuccess', { count: deletedCount }));
      fetchSnapshots();
    } catch (err) {
      console.error('Error deleting selected snapshots:', err);
      setError(t('deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleAnalyze = async () => {
    if (!currentNursery || !showAnalyzeModal || !selectedPromptId) return;
    try {
      setAnalyzing(true);
      setError(null);
      const res = await snapshotAnalysisAPI.analyze(
        currentNursery.nursery_id,
        showAnalyzeModal.id,
        { prompt_id: selectedPromptId }
      );
      setAnalysisResult(res.data);
      setSnapshots(prev => prev.map(s => 
        s.id === showAnalyzeModal.id 
          ? { ...s, analysis_count: (s.analysis_count ?? 0) + 1 } 
          : s
      ));
    } catch (err: any) {
      console.error('Error analyzing snapshot:', err);
      const isTimeout = err?.response?.status === 504 || err?.response?.status === 408;
      setError(isTimeout ? ta('analyzeTimeout') : ta('analyzeFailed'));
      setShowAnalyzeModal(null);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleShowHistory = async (snapshot: Snapshot) => {
    if (!currentNursery) return;
    setAnalysisHistory([]);
    setShowHistoryModal(snapshot);
    setLoadingHistory(true);
    try {
      const res = await snapshotAnalysisAPI.getHistory(currentNursery.nursery_id, snapshot.id);
      setAnalysisHistory(res.data || []);
    } catch (err) {
      console.error('Error fetching analysis history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleImageError = (id: number) => {
    setFailedImages(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const filteredCameras = formState.classroom_id
    ? cameras.filter(cam => cam.classroom_id === parseInt(formState.classroom_id))
    : cameras;

  if (!canViewSnapshots) {
    return (
      <Layout title={t('title')}>
        <Container className="mt-4">
          <Alert variant="danger">{t('accessDenied')}</Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3 pb-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="fw-bold text-primary mb-0">{t('title')}</h2>
          {canDeleteSnapshots && snapshots.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              {selectionMode && (
                <>
                  <span className="text-muted small">{t('selectedCount', { count: selectedSnapshots.size })}</span>
                  <Button variant="outline-secondary" size="sm" onClick={selectAllSnapshots}>
                    {t('selectAll')}
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={deselectAllSnapshots}>
                    {t('deselectAll')}
                  </Button>
                </>
              )}
              <Button variant="outline-secondary" onClick={toggleSelectionMode}>
                {selectionMode ? t('exitSelectMode') : t('selectMode')}
              </Button>
              {selectionMode ? (
                <Button
                  variant="outline-danger"
                  onClick={() => setShowSelectedDeleteModal(true)}
                  disabled={selectedSnapshots.size === 0}
                >
                  {t('deleteSelected')}
                </Button>
              ) : (
                <Button variant="outline-danger" onClick={() => setShowBulkDeleteModal(true)}>
                  {t('bulkDelete')}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Row className="align-items-end g-3">
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-bold">{t('classroom')}</Form.Label>
                  <Form.Select 
                    value={formState.classroom_id} 
                    onChange={(e) => handleFilterChange('classroom_id', e.target.value)}
                  >
                    <option value="">{t('allClassrooms')}</option>
                    {classrooms.map(classroom => (
                      <option key={classroom.id} value={classroom.id}>{classroom.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-bold">{tc('labels.camera')}</Form.Label>
                  <Form.Select 
                    value={formState.camera_id} 
                    onChange={(e) => handleFilterChange('camera_id', e.target.value)}
                  >
                    <option value="">{t('allCameras')}</option>
                    {filteredCameras.map(cam => (
                      <option key={cam.id} value={cam.id}>{cam.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-bold">{tc('labels.fromDate')}</Form.Label>
                  <Form.Control 
                    type="date" 
                    value={formState.date_from} 
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-bold">{tc('labels.toDate')}</Form.Label>
                  <Form.Control 
                    type="date" 
                    value={formState.date_to} 
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <div className="d-flex gap-2">
                  <Button variant="primary" className="flex-grow-1" onClick={applyFilters}>
                    {tc('buttons.apply')}
                  </Button>
                  <Button variant="outline-secondary" onClick={clearFilters}>
                    {tc('buttons.clear')}
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : snapshots.length === 0 ? (
          <Card className="text-center p-5 border-0 shadow-sm">
            <Card.Body>
              <div className="mb-4">
                <i className="bi bi-images text-muted" style={{ fontSize: '3rem' }}></i>
              </div>
              <h4>{appliedFilters.camera_id || appliedFilters.classroom_id || appliedFilters.date_from ? t('noMatchFilters') : t('noSnapshots')}</h4>
              <p className="text-muted mb-4">{t('noSnapshotsHint')}</p>
              {!appliedFilters.camera_id && !appliedFilters.classroom_id && !appliedFilters.date_from && (
                <Button variant="primary" href={`/nursery/${currentNursery?.nursery_id}/settings/snapshot-settings`}>
                  {tc('nav.snapshotSettings')}
                </Button>
              )}
            </Card.Body>
          </Card>
        ) : (
          <>
            <Row className="g-4">
              {snapshots.map(snapshot => (
                <Col key={snapshot.id} xs={6} sm={4} md={3} lg={2}>
                  <Card className="h-100 border-0 shadow-sm snapshot-card">
                    <div 
                      className="position-relative overflow-hidden bg-light"
                      style={{ paddingBottom: '75%', cursor: 'pointer' }}
                      onClick={() => selectionMode ? toggleSnapshotSelection(snapshot.id) : setSelectedSnapshot(snapshot)}
                    >
                      {selectionMode && (
                        <div className="position-absolute top-0 start-0 m-2" style={{ zIndex: 2 }}>
                          <Form.Check
                            type="checkbox"
                            checked={selectedSnapshots.has(snapshot.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleSnapshotSelection(snapshot.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                      {failedImages.has(snapshot.id) ? (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-muted p-2">
                          <i className="bi bi-image-fill mb-1"></i>
                          <small style={{ fontSize: '10px' }}>{t('imageLoadError')}</small>
                        </div>
                      ) : (
                        <img 
                          src={snapshot.image_url} 
                          alt={snapshot.camera_name}
                          className="position-absolute top-0 start-0 w-100 h-100 object-fit-cover"
                          onError={() => handleImageError(snapshot.id)}
                        />
                      )}
                      {(snapshot.analysis_count ?? 0) > 0 && (
                        <span 
                          className="position-absolute top-0 end-0 m-1 badge rounded-pill bg-success"
                          style={{ fontSize: '10px', zIndex: 1 }}
                        >
                          <i className="bi bi-check-circle-fill me-1"></i>
                          {snapshot.analysis_count}
                        </span>
                      )}
                    </div>
                    <Card.Body className="p-2">
                      <div className="small fw-bold text-truncate mb-1">{snapshot.camera_name}</div>
                      {snapshot.classroom_name && (
                        <div className="text-muted text-truncate" style={{ fontSize: '11px' }}>
                          {snapshot.classroom_name}
                        </div>
                      )}
                      <div className="text-muted" style={{ fontSize: '11px' }}>
                        {formatDateTimeInNurseryTimezone(snapshot.captured_at, currentNursery?.timezone || 'UTC')}
                      </div>
                      <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
                        {canAnalyzeSnapshots && prompts.length > 0 && (
                          <Button 
                            variant="link" size="sm"
                            className={`${(snapshot.analysis_count ?? 0) > 0 ? 'text-success' : 'text-primary'} p-0 text-decoration-none`}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setAnalysisResult(null);
                              setSelectedPromptId(null);
                              setShowAnalyzeModal(snapshot); 
                            }}
                          >
                            <i className="bi bi-robot"></i> {ta('analyze')}
                          </Button>
                        )}
                        <Button variant="link" size="sm" 
                          className={`${(snapshot.analysis_count ?? 0) > 0 ? 'text-success' : 'text-secondary'} p-0 text-decoration-none`}
                          onClick={(e) => { e.stopPropagation(); handleShowHistory(snapshot); }}>
                          <i className="bi bi-clock-history"></i> {ta('history')}
                        </Button>
                        {canDeleteSnapshots && (
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="text-danger p-0 text-decoration-none"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeleteModal(snapshot.id);
                            }}
                          >
                            <i className="bi bi-trash"></i> {tc('buttons.delete')}
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {pagination && pagination.last_page > 1 && (
              <div className="d-flex justify-content-center mt-5">
                <Pagination>
                  <Pagination.First onClick={() => handlePageChange(1)} disabled={pagination.current_page === 1} />
                  <Pagination.Prev 
                    disabled={pagination.current_page === 1}
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                  />
                  {[...Array(Math.min(5, pagination.last_page))].map((_, i) => {
                    let pageNum;
                    if (pagination.last_page <= 5) pageNum = i + 1;
                    else if (pagination.current_page <= 3) pageNum = i + 1;
                    else if (pagination.current_page >= pagination.last_page - 2) pageNum = pagination.last_page - 4 + i;
                    else pageNum = pagination.current_page - 2 + i;

                    return (
                      <Pagination.Item 
                        key={pageNum}
                        active={pageNum === pagination.current_page}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next 
                    disabled={pagination.current_page === pagination.last_page}
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                  />
                  <Pagination.Last onClick={() => handlePageChange(pagination.last_page)} disabled={pagination.current_page === pagination.last_page} />
                </Pagination>
              </div>
            )}
          </>
        )}

        {/* Full Image Modal */}
        <Modal 
          show={!!selectedSnapshot} 
          onHide={() => setSelectedSnapshot(null)} 
          size="lg" 
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title className="h6">
              {selectedSnapshot?.camera_name} - {selectedSnapshot && formatDateTimeInNurseryTimezone(selectedSnapshot.captured_at, currentNursery?.timezone || 'UTC')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0 bg-dark text-center">
            {selectedSnapshot && (
              <img 
                src={selectedSnapshot.image_url} 
                alt={selectedSnapshot.camera_name}
                className="img-fluid"
                style={{ maxHeight: '80vh', objectFit: 'contain' }}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            {canAnalyzeSnapshots && prompts.length > 0 && selectedSnapshot && (
              <Button 
                variant={(selectedSnapshot.analysis_count ?? 0) > 0 ? 'outline-success' : 'outline-primary'}
                onClick={() => { 
                  setAnalysisResult(null);
                  setSelectedPromptId(null);
                  setShowAnalyzeModal(selectedSnapshot); 
                  setSelectedSnapshot(null); 
                }}
              >
                <i className="bi bi-robot"></i> {ta('analyze')}
              </Button>
            )}
            {selectedSnapshot && (
              <Button variant={(selectedSnapshot.analysis_count ?? 0) > 0 ? 'outline-success' : 'outline-secondary'}
                onClick={() => { handleShowHistory(selectedSnapshot); setSelectedSnapshot(null); }}>
                <i className="bi bi-clock-history"></i> {ta('history')}
              </Button>
            )}
            {canDeleteSnapshots && selectedSnapshot && (
              <Button 
                variant="outline-danger" 
                onClick={() => {
                  setShowDeleteModal(selectedSnapshot.id);
                  setSelectedSnapshot(null);
                }}
              >
                {t('deleteSnapshot')}
              </Button>
            )}
            <Button variant="secondary" onClick={() => setSelectedSnapshot(null)}>
              {tc('buttons.close')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Analyze Snapshot Modal */}
        <Modal 
          show={!!showAnalyzeModal} 
          onHide={() => { setShowAnalyzeModal(null); setAnalysisResult(null); setSelectedPromptId(null); }} 
          size="lg" 
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{ta('analyzeSnapshot')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {analysisResult ? (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Badge bg="success">{analysisResult.prompt_name}</Badge>
                  <small className="text-muted">
                    {analysisResult.model_used} · {analysisResult.tokens_used ?? '—'} {ta('tokensUsed')} · {formatDateTimeInNurseryTimezone(analysisResult.created_at, currentNursery?.timezone || 'UTC')}
                  </small>
                </div>
                <div className="border rounded p-3 bg-light markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{analysisResult.result}</ReactMarkdown>
                </div>
                <div className="mt-3">
                  <Button variant="outline-primary" onClick={() => { setAnalysisResult(null); setSelectedPromptId(null); }}>
                    {ta('analyzeAgain')}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-muted mb-3">{ta('selectPromptHint')}</p>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">{ta('selectPrompt')}</Form.Label>
                  <Form.Select value={selectedPromptId || ''} onChange={(e) => setSelectedPromptId(parseInt(e.target.value) || null)}>
                    <option value="">{ta('choosePrompt')}</option>
                    {prompts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            {!analysisResult && (
              <Button variant="primary" disabled={!selectedPromptId || analyzing} onClick={handleAnalyze}>
                {analyzing ? <><Spinner animation="border" size="sm" /> {ta('analyzing')}</> : ta('runAnalysis')}
              </Button>
            )}
            <Button variant="secondary" onClick={() => { setShowAnalyzeModal(null); setAnalysisResult(null); setSelectedPromptId(null); }}>
              {tc('buttons.close')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Analysis History Modal */}
        <Modal show={!!showHistoryModal} onHide={() => setShowHistoryModal(null)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>{ta('analysisHistory')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingHistory ? (
              <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
            ) : analysisHistory.length === 0 ? (
              <p className="text-muted text-center py-4">{ta('noAnalysisHistory')}</p>
            ) : (
              analysisHistory.map(analysis => (
                <Card key={analysis.id} className="mb-3 border">
                  <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2">
                    <div>
                      <Badge bg="primary" className="me-2">{analysis.prompt_name}</Badge>
                      <small className="text-muted">{ta('analyzedBy')} {analysis.analyzed_by}</small>
                    </div>
                    <small className="text-muted">
                      {formatDateTimeInNurseryTimezone(analysis.created_at, currentNursery?.timezone || 'UTC')} · {analysis.model_used} · {analysis.tokens_used ?? '—'} {ta('tokensUsed')}
                    </small>
                  </Card.Header>
                  <Card.Body className="markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{analysis.result}</ReactMarkdown>
                  </Card.Body>
                </Card>
              ))
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowHistoryModal(null)}>
              {tc('buttons.close')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirm Modal */}
        <Modal show={!!showDeleteModal} onHide={() => setShowDeleteModal(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('deleteSnapshot')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{t('deleteConfirm')}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(null)} disabled={deleting}>
              {tc('buttons.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteSingle} disabled={deleting}>
              {deleting ? <Spinner animation="border" size="sm" /> : tc('buttons.delete')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Bulk Delete Modal */}
        <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('bulkDeleteTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="warning">
              {t('bulkDeleteConfirm')}
            </Alert>
            <div className="mb-3">
              <strong>{t('classroom')}:</strong> {formState.classroom_id ? classrooms.find(c => c.id === parseInt(formState.classroom_id))?.name : t('allClassrooms')}
            </div>
            <div className="mb-3">
              <strong>{tc('labels.camera')}:</strong> {formState.camera_id ? cameras.find(c => c.id === parseInt(formState.camera_id))?.name : t('allCameras')}
            </div>
            <div className="mb-3">
              <strong>{tc('labels.fromDate')}:</strong> {formState.date_from || '-'}
            </div>
            <div className="mb-3">
              <strong>{tc('labels.toDate')}:</strong> {formState.date_to || '-'}
            </div>
            {(!formState.date_from || !formState.date_to) && (
              <div className="text-danger small">{t('selectDateRange')}</div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)} disabled={deleting}>
              {tc('buttons.cancel')}
            </Button>
            <Button 
              variant="danger" 
              onClick={handleBulkDelete} 
              disabled={deleting || !formState.date_from || !formState.date_to}
            >
              {deleting ? <Spinner animation="border" size="sm" /> : t('bulkDelete')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Selected Modal */}
        <Modal show={showSelectedDeleteModal} onHide={() => setShowSelectedDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('deleteSelected')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="warning">
              {t('deleteSelectedConfirm', { count: selectedSnapshots.size })}
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSelectedDeleteModal(false)} disabled={deleting}>
              {tc('buttons.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteSelected} disabled={deleting || selectedSnapshots.size === 0}>
              {deleting ? <Spinner animation="border" size="sm" /> : t('deleteSelected')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>

      <style>{`
        .snapshot-card:hover {
          transform: translateY(-5px);
          transition: transform 0.2s ease-in-out;
        }
        .object-fit-cover {
          object-fit: cover;
        }
        .object-fit-contain {
          object-fit: contain;
        }
        .markdown-content table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1rem;
        }
        .markdown-content th, .markdown-content td {
          border: 1px solid rgba(15,23,42,0.08);
          padding: 8px 12px;
          text-align: left;
        }
        .markdown-content th {
          background-color: #f8f9fa;
          font-weight: 600;
        }
      `}</style>
    </Layout>
  );
};

export default CameraSnapshotsPage;
